import React, { useState } from 'react';
import { DEFAULT_FLOOR_STYLE, FLOOR_LEVEL_MIN_CM, FLOOR_LEVEL_MAX_CM } from './Floor3D.jsx';
import './floorStyleBox.scss';

// جنس‌های کف: «رنگ» (رنگِ واقعی) + عکس‌های واقعیِ PBR (Floor3D.jsx → pbrTextures.jsx)
const FLOOR_TYPES = [
  { value: 'paint', label: 'رنگ' },
  { value: 'parquetPbr', label: 'پارکت ✦' },
  { value: 'laminateLightPbr', label: 'لمینتِ روشن ✦' },
  { value: 'laminateDarkPbr', label: 'لمینتِ تیره ✦' },
  { value: 'ceramicPbr', label: 'سرامیک ✦' },
  { value: 'stoneSlabPbr', label: 'سنگِ رگه‌دار (مرمر) ✦' },
  { value: 'stonePbr', label: 'سنگِ لکه‌دار (گرانیت) ✦' },
  { value: 'travertinePbr', label: 'تراورتن ✦' },
  { value: 'carpetPbr', label: 'موکت ✦' },
  { value: 'concretePbr', label: 'بتن/سیمان ✦' },
];

const TYPE_HINTS = {
  paint: 'رنگِ واقعی: بافتِ ملایمِ سطحِ رنگ‌شده (مثلاً کفِ اپوکسی/رنگ‌شده)، با هر رنگی که انتخاب کنی.',
  parquetPbr: 'عکسِ واقعیِ پارکتِ چوبیِ جناغی (استخوان‌ماهی).',
  laminateLightPbr: 'عکسِ واقعیِ لمینت با تخته‌های صاف و بلند — چوبِ روشن.',
  laminateDarkPbr: 'عکسِ واقعیِ لمینت با تخته‌های صاف و بلند — چوبِ تیره.',
  ceramicPbr: 'عکسِ واقعیِ کاشیِ سفیدِ براق، با بندکشیِ واقعی.',
  stoneSlabPbr: 'عکسِ واقعیِ مرمرِ رگه‌دار.',
  stonePbr: 'عکسِ واقعیِ گرانیتِ لکه‌دار.',
  travertinePbr: 'عکسِ واقعیِ سنگِ تراورتنِ کرم با رگه‌های افقی — رایجِ کف و حمام.',
  carpetPbr: 'عکسِ واقعیِ موکتِ پُرزدار — رنگش کاملاً از «رنگ» می‌آید.',
  concretePbr: 'عکسِ واقعیِ بتن/سیمانِ خاکستری.',
};

const PATTERN_HINTS = {
  parquetPbr: 'اندازه‌ی هر ستونِ الگوی جناغی — عددِ کوچک‌تر یعنی تخته‌های ریزتر.',
  laminateLightPbr: 'اندازه‌ی تخته‌ها — هر تخته تقریباً نصفِ این عدد پهنا دارد (۴۰ ≈ تخته‌ی ۲۰ سانتی‌متری).',
  laminateDarkPbr: 'اندازه‌ی تخته‌ها — هر تخته تقریباً نصفِ این عدد پهنا دارد (۴۰ ≈ تخته‌ی ۲۰ سانتی‌متری).',
  ceramicPbr: 'اندازه‌ی هر کاشی — عددِ کوچک‌تر یعنی کاشی‌های ریزتر.',
};
const DEFAULT_PATTERN_HINT = 'اندازه‌ی کلِ عکس روی کف — عددِ کوچک‌تر یعنی جزئیاتِ ریزتر و تکرارِ بیشتر.';

const MAIN_COLOR_PRESETS = ['#ffffff', '#e7dcc9', '#c9a876', '#8a5a3b', '#d9d9d9', '#3a3a3a'];

const TABS = [
  { id: 'type', label: 'جنس' },
  { id: 'material', label: 'رنگ' },
  { id: 'pattern', label: 'اندازه' },
  { id: 'level', label: 'ارتفاع' },
];

const faNum = (n) => Math.abs(Math.round(n)).toLocaleString('fa-IR');
function levelText(levelCm) {
  if (!levelCm) return 'هم‌سطحِ بقیه‌ی خانه';
  return levelCm > 0 ? `${faNum(levelCm)} سانتی‌متر بالاتر` : `${faNum(levelCm)} سانتی‌متر پایین‌تر`;
}

const TILE_MIN_CM = 15;
const TILE_MAX_CM = 90;

function ColorField({ label, value, presets, onChange }) {
  return (
    <div className="floor-style-box__field">
      {label && <span className="floor-style-box__label">{label}</span>}
      <div className="floor-style-box__swatches">
        {presets.map((c) => (
          <button
            key={c}
            type="button"
            className={`floor-style-box__swatch ${value === c ? 'is-active' : ''}`}
            style={{ background: c }}
            onClick={() => onChange(c)}
          />
        ))}
        <input
          type="color"
          className="floor-style-box__custom-color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </div>
  );
}

// پنلِ ادیتِ کفِ یک اتاق در حالت سه‌بعدی — سه تب: جنس، رنگ، اندازه‌ی الگو (فقط برای جنس‌های عکس‌دار)
function FloorStyleBox({ style, onChangeType, onChangeColor, onChangeSize, onChangeLevel, onClose }) {
  const [activeTab, setActiveTab] = useState('type');
  const s = style || DEFAULT_FLOOR_STYLE;
  const tileCm = s.tileCm || 40;
  const isPhoto = s.type !== 'paint';
  const levelCm = s.levelCm || 0;
  const setLevel = onChangeLevel || ((v) => onChangeSize({ levelCm: v }));

  return (
    <div className="floor-style-box">
      <div className="floor-style-box__header">
        <span>تنظیماتِ کفِ این اتاق</span>
        <button type="button" onClick={onClose}>
          بستن
        </button>
      </div>

      <div className="floor-style-box__tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={activeTab === tab.id ? 'is-active' : ''}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'type' && (
        <div className="floor-style-box__panel">
          <div className="floor-style-box__pills floor-style-box__pills--wrap">
            {FLOOR_TYPES.map((t) => (
              <button
                key={t.value}
                type="button"
                className={s.type === t.value ? 'is-active' : ''}
                onClick={() => onChangeType(t.value)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <p className="floor-style-box__hint">{TYPE_HINTS[s.type] || TYPE_HINTS.paint}</p>
          <p className="floor-style-box__hint">
            این کف فقط روی همین اتاق اعمال می‌شود؛ برای اتاق‌های دیگر جدا باید تنظیم شود.
          </p>
        </div>
      )}

      {activeTab === 'material' && (
        <div className="floor-style-box__panel">
          <ColorField
            label={isPhoto ? 'رنگ (روی عکسِ واقعی اعمال می‌شود)' : 'رنگ'}
            value={s.color || '#e7dcc9'}
            presets={MAIN_COLOR_PRESETS}
            onChange={onChangeColor}
          />
          {isPhoto && (
            <p className="floor-style-box__hint">
              سفید = رنگِ طبیعیِ خودِ عکس؛ هر رنگِ دیگری روی عکس ضرب می‌شود و آن را تیره‌تر/رنگی‌تر می‌کند.
            </p>
          )}
        </div>
      )}

      {activeTab === 'pattern' && (
        <div className="floor-style-box__panel">
          {isPhoto ? (
            <>
              <div className="floor-style-box__field">
                <span className="floor-style-box__label">اندازه‌ی الگو: {tileCm} سانتی‌متر</span>
                <input
                  type="range"
                  min={TILE_MIN_CM}
                  max={TILE_MAX_CM}
                  step={5}
                  value={tileCm}
                  onChange={(e) => onChangeSize({ tileCm: Number(e.target.value) })}
                  className="floor-style-box__slider"
                />
              </div>
              <p className="floor-style-box__hint">{PATTERN_HINTS[s.type] || DEFAULT_PATTERN_HINT}</p>
            </>
          ) : (
            <p className="floor-style-box__hint">اندازه‌ی بافتِ «رنگ» ثابت و متناسب با ابعادِ واقعی است.</p>
          )}
        </div>
      )}

      {activeTab === 'level' && (
        <div className="floor-style-box__panel">
          <div className="floor-style-box__field">
            <span className="floor-style-box__label">ارتفاعِ کف: {levelText(levelCm)}</span>
            <input
              type="range"
              min={FLOOR_LEVEL_MIN_CM}
              max={FLOOR_LEVEL_MAX_CM}
              step={5}
              value={levelCm}
              onChange={(e) => setLevel(Number(e.target.value))}
              className="floor-style-box__slider"
            />
          </div>
          <div className="floor-style-box__pills">
            {[-10, 0, 10, 15].map((v) => (
              <button key={v} type="button" className={levelCm === v ? 'is-active' : ''} onClick={() => setLevel(v)}>
                {v === 0 ? 'هم‌سطح' : `${v > 0 ? '+' : '−'}${faNum(v)}`}
              </button>
            ))}
          </div>
          <p className="floor-style-box__hint">
            اختلافِ سطحِ این اتاق با بقیه‌ی خانه — مثلاً آشپزخانه ۱۰ سانت بالاتر، یا حمام/بالکن کمی پایین‌تر. پله‌ی
            کنارِ درها و بازشوها خودکار ساخته می‌شود و در حالتِ اول‌شخص موقعِ رد شدن، بالا/پایین می‌روی.
          </p>
        </div>
      )}
    </div>
  );
}

export default FloorStyleBox;